/**
 * Horizontal Swiper Component
 * Initializes Swiper.js for horizontal swiper sections
 */

class HorizontalSwiper {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    this.swiper = null;
    this.config = this.getConfig();
    this.init();
  }

  getConfig() {
    // Check if the container itself has the data attribute
    if (this.container.hasAttribute("data-swiper-config")) {
      try {
        return JSON.parse(this.container.dataset.swiperConfig || "{}");
      } catch (error) {
        console.warn("Invalid Swiper config:", error);
        return {};
      }
    }

    // Otherwise, look for a child element with the attribute
    const configData = this.container.querySelector("[data-swiper-config]");
    if (!configData || !(configData instanceof HTMLElement)) return {};

    try {
      return JSON.parse(configData.dataset.swiperConfig || "{}");
    } catch (error) {
      console.warn("Invalid Swiper config:", error);
      return {};
    }
  }

  init() {
    if (typeof Swiper === "undefined") {
      console.warn("Swiper.js is not loaded");
      return;
    }

    // Check if this is a full-height-hero-swiper (needs single slide view)
    const isFullHeightHero = this.container.classList.contains("full-height-hero-swiper__swiper");

    // Check if pagination icons are enabled
    const paginationEl = this.container.querySelector(".swiper-pagination");
    const usePaginationIcons = paginationEl && paginationEl.classList.contains("swiper-pagination--icons");

    // Default configuration
    const defaultConfig = {
      slidesPerView: "auto",
      spaceBetween: 100,
      loop: true,
      autoplay: false,
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      breakpoints: {
        320: {
          slidesPerView: 1,
          spaceBetween: 10,
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 30,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 100,
        },
      },
    };

    // For full-height-hero-swiper, prioritize the config from data attribute
    // Otherwise merge custom config with defaults
    let config;
    if (isFullHeightHero) {
      if (this.config && Object.keys(this.config).length > 0) {
        // Use the config directly from data attribute for full-height-hero-swiper
        // Ensure breakpoints are properly structured (convert string keys to numbers if needed)
        config = { ...this.config };
        if (config.breakpoints) {
          const breakpoints = {};
          Object.keys(config.breakpoints).forEach((key) => {
            // Convert string keys to numbers for Swiper
            const numKey = parseInt(key, 10);
            if (!isNaN(numKey)) {
              breakpoints[numKey] = { ...config.breakpoints[key] };
            } else {
              breakpoints[key] = { ...config.breakpoints[key] };
            }
          });
          config.breakpoints = breakpoints;
        }
      } else {
        // Fallback: if no config found, use minimal config with 1 slide
        config = {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: true,
          pagination: {
            el: ".swiper-pagination",
            clickable: true,
          },
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },
          breakpoints: {
            320: { slidesPerView: 1, spaceBetween: 0 },
            768: { slidesPerView: 1, spaceBetween: 0 },
            1024: { slidesPerView: 1, spaceBetween: 0 },
          },
        };
      }
    } else {
      // For other swipers, merge with defaults
      let baseConfig = this.config && Object.keys(this.config).length > 0 ? this.config : defaultConfig;
      config = {
        ...defaultConfig,
        ...baseConfig,
        // For breakpoints, if custom config has them, use them entirely, otherwise merge
        breakpoints: baseConfig.breakpoints || {
          ...defaultConfig.breakpoints,
          ...(this.config.breakpoints || {}),
        },
      };
    }

    // Handle custom pagination icons rendering
    if (usePaginationIcons && config.pagination) {
      config.pagination.renderBullet = function (index, className) {
        return '<span class="' + className + ' swiper-pagination-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="5"></circle></svg></span>';
      };
      // Remove paginationType from config as it's not a valid Swiper option
      if (config.pagination.paginationType) {
        delete config.pagination.paginationType;
      }
    }

    // Initialize Swiper
    this.swiper = new Swiper(this.container, config);

    // Handle custom arrow navigation (for slideshow-style arrows in full-height-hero-swiper)
    if (isFullHeightHero) {
      const arrowsContainer = this.container.parentElement?.querySelector(".full-height-hero-swiper__arrows");
      if (arrowsContainer) {
        const prevButton = arrowsContainer.querySelector(".slideshow-control--previous");
        const nextButton = arrowsContainer.querySelector(".slideshow-control--next");

        if (prevButton) {
          prevButton.addEventListener("click", (/** @type {MouseEvent} */ e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.swiper) {
              this.swiper.slidePrev();
            }
          });
        }

        if (nextButton) {
          nextButton.addEventListener("click", (/** @type {MouseEvent} */ e) => {
            e.preventDefault();
            e.stopPropagation();
            if (this.swiper) {
              this.swiper.slideNext();
            }
          });
        }
      }
    }

    // Handle autoplay pause on hover
    if (config.autoplay && this.swiper && this.swiper.autoplay) {
      this.container.addEventListener("mouseenter", () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.stop();
        }
      });

      this.container.addEventListener("mouseleave", () => {
        if (this.swiper && this.swiper.autoplay) {
          this.swiper.autoplay.start();
        }
      });
    }

    // Handle resize events
    this._onResize = () => {
      if (this.swiper) {
        this.swiper.update();
      }
    };
    window.addEventListener("resize", this._onResize);
  }

  destroy() {
    window.removeEventListener("resize", this._onResize);
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

// Initialize all horizontal swipers on the page
document.addEventListener("DOMContentLoaded", () => {
  const swiperContainers = document.querySelectorAll(".horizontal-swiper__swiper, .full-height-hero-swiper__swiper");

  swiperContainers.forEach((container) => {
    if (container instanceof HTMLElement) {
      container.swiperInstance = new HorizontalSwiper(container);
    }
  });
});

// Handle dynamic content loading (for Shopify's section rendering)
document.addEventListener("shopify:section:load", (event) => {
  if (event.target && event.target instanceof HTMLElement) {
    const swiperContainer = event.target.querySelector(".horizontal-swiper__swiper, .full-height-hero-swiper__swiper");
    if (swiperContainer && swiperContainer instanceof HTMLElement) {
      swiperContainer.swiperInstance = new HorizontalSwiper(swiperContainer);
    }
  }
});

// Handle section unload
document.addEventListener("shopify:section:unload", (event) => {
  if (event.target && event.target instanceof HTMLElement) {
    const swiperContainer = event.target.querySelector(".horizontal-swiper__swiper, .full-height-hero-swiper__swiper");
    if (swiperContainer && swiperContainer instanceof HTMLElement && swiperContainer.swiperInstance) {
      swiperContainer.swiperInstance.destroy();
      delete swiperContainer.swiperInstance;
    }
  }
});

export default HorizontalSwiper;
